import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { UsurpCardList } from 'site-modules/shared/components/inventory/usurp-card-list/usurp-card-list';
import { ZipLongDistanceConfirmProvider } from 'site-modules/shared/components/lead-form/zip-long-distance-confirm/zip-long-distance-confirm-provider';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryModel, buildSearchResultsBackfillPath } from 'client/data/models/inventory';
import { EventToolbox } from 'client/utils/event-toolbox';
import { ACTIONS } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import searchIllustration from 'client/images/search-illustration.svg';
import {
  getExpandedRadius,
  SHOP_5016_STRATEGIES,
  SHOP_5016_TRACKING,
} from 'site-modules/inventory/utils/shop-5016-backfill-utils';
import { LoadingBackfill } from './loading-backfill';

export function Shop5016BackfillUI({
  strategy,
  cardListProps,
  fallback = null,
  backfillData = undefined,
  paginationComponent,
}) {
  const tracking = SHOP_5016_TRACKING[strategy];
  const hasResolved = typeof backfillData !== 'undefined';
  const inventory = backfillData?.inventory || [];

  const additionalInventoryCount = inventory.length;
  const radius = backfillData?.radius;

  useEffect(() => {
    if (!hasResolved || !additionalInventoryCount) return;

    EventToolbox.fireCustomEvent(ACTIONS.INVENTORY_LIST_NEW_FIELDS, {
      inventory,
      creativeId: tracking.widget,
      radius: getExpandedRadius(radius) || radius,
      totalCount: additionalInventoryCount,
      attributes: {},
      defaults: { defaultNoImageType: 'silhouette' },
    });
  }, [hasResolved, inventory, additionalInventoryCount, radius, tracking.widget]);

  const primaryList = <UsurpCardList {...cardListProps} />;
  const primaryListWithProvider = <ZipLongDistanceConfirmProvider>{primaryList}</ZipLongDistanceConfirmProvider>;

  if (hasResolved && !additionalInventoryCount) {
    return (
      <>
        {primaryListWithProvider}
        {fallback}
        {paginationComponent}
      </>
    );
  }

  if (!hasResolved) {
    return (
      <>
        {primaryListWithProvider}
        {paginationComponent}
        <LoadingBackfill widgetName={tracking.widget} />
      </>
    );
  }

  const heading =
    strategy === SHOP_5016_STRATEGIES.RADIUS
      ? 'We found more listings nearby.'
      : 'There are no more results. Check out these similar matches.';

  return (
    <>
      {primaryListWithProvider}
      {cardListProps.inventoryCount === 0 && (
        <img
          className="d-block mx-auto"
          height={120}
          width={204}
          src={searchIllustration}
          alt={'No matching inventory found.'}
          loading="lazy"
        />
      )}
      {paginationComponent}
      <ZipLongDistanceConfirmProvider>
        <section data-tracking-parent={tracking.widget} aria-labelledby={`${tracking.widget}-heading`}>
          <h3 id={`${tracking.widget}-heading`} className="mt-2 mb-1">
            {heading}
          </h3>
          <UsurpCardList
            {...cardListProps}
            inventory={inventory}
            primaryInventoryCount={additionalInventoryCount}
            disableAds
            trackingParent={tracking.card}
            trackingCreativeId={tracking.card}
          />
        </section>
      </ZipLongDistanceConfirmProvider>
    </>
  );
}

Shop5016BackfillUI.propTypes = {
  strategy: PropTypes.oneOf(Object.values(SHOP_5016_STRATEGIES)).isRequired,
  cardListProps: PropTypes.shape({}).isRequired,
  fallback: PropTypes.node,
  backfillData: PropTypes.shape({}),
  paginationComponent: PropTypes.node,
};

export const Shop5016Backfill = connectToModel(Shop5016BackfillUI, {
  backfillData: bindToPath(buildSearchResultsBackfillPath, InventoryModel, null, false),
});
